/**
 * Utilities to help the cookie library function
 *
 * @author   Evan Cooper <evcooper@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 *
 */
/* eslint-disable consistent-return */

import Cookies from 'js-cookie';

export const cookiePreferenceLevelTypes = Object.freeze({
  NECESSARY: 'NECESSARY',
  PERSONALIZATION: 'PERSONALIZATION',
  ANALYTICS: 'ANALYTICS',
  ADVERTISING: 'ADVERTISING',
  ADMINISTRATIVE: 'NECESSARY',
  // TODO: Remove after conversion work is complete
  DANGEROUSLY_UNSET_INTENT: 'DANGEROUSLY_UNSET_INTENT',
});

export const MINIMUM_USER_PREFERENCES = {
  NECESSARY: true,
  ADMINISTRATIVE: true,
  ANALYTICS: false,
  PERSONALIZATION: false,
  ADVERTISING: false,
};

export const MAXIMUM_USER_PREFERENCES = {
  NECESSARY: true,
  ADMINISTRATIVE: true,
  ANALYTICS: true,
  PERSONALIZATION: true,
  ADVERTISING: true,
};

export const getIsFieldValidSubcookieOfCookieKey = (
  field,
  cookieKey,
  subcookieRegistry
) => {
  const validSubcookiePatterns = subcookieRegistry[cookieKey];
  if (typeof validSubcookiePatterns === 'undefined') {
    return false;
  }

  for (const validSubcookiePattern of validSubcookiePatterns) {
    const re = new RegExp(`^${validSubcookiePattern}$`);
    if (re.test(field)) {
      return true;
    }
  }

  return false;
};

/**
 * Helper function to hold cookie expiration logic
 *
 * @param {string} cookieName - name of the cookie to expire
 * @param {boolean} shouldExpireWithPaths - boolean to indicate if we should try expiring the cookie with path combinations
 */
export const expireCookie = (cookieName, shouldExpireWithPaths = false) => {
  // need to URI encode the cookie name in case it contains special characters
  // cookie names are URI encoded when they are set with other libraries such as js-cookie
  const cookieBase = `${encodeURI(
    cookieName
  )}=; expires=Thu, 01-Jan-1970 00:00:01 GMT;`;
  const d = window.location.hostname.split('.');
  document.cookie = `${cookieBase} path=/;`;
  if (shouldExpireWithPaths) {
    clearAllPaths(cookieBase);
  }
  while (d.length > 0) {
    if (shouldExpireWithPaths) {
      // expire cookies with and without leading period on domains
      clearAllPaths(`${cookieBase} domain=.${d.join('.')};`);
      clearAllPaths(`${cookieBase} domain=${d.join('.')};`);
    }
    // expire cookies with and without leading period on domains
    document.cookie = `${cookieBase} path=/; domain=.${d.join('.')};`;
    document.cookie = `${cookieBase} path=/; domain=${d.join('.')};`;
    d.shift();
  }
};

/**
 * Helper function to clear paths for a given cookie
 *
 * @param cookieBase
 */
const clearAllPaths = (cookieBase) => {
  const p = window.location.pathname.split('/');
  while (p.length > 0) {
    document.cookie = `${cookieBase} path=${p.join('/')};`;
    p.pop();
  }
};

/**
 * Log any cookie we failed to expire
 *
 * @param {Object} expiredCookies - object containing expired cookies
 * @param {Object} logger - a logger instance
 */
export const logCookiesFailedToExpire = (expiredCookies, logger) => {
  for (const [cookieName, cookieValue] of Object.entries(Cookies.get())) {
    if (expiredCookies[cookieName]) {
      logger.warn({
        name: 'Cookie Circumvented Expiration',
        message: `Cookie ${cookieName} circumvented our expiration logic.`,
        data: {
          cookieName,
          cookieValue,
        },
      });
    }
  }
};

/**
 * Log cookies that are not in the taxonomy
 *
 * @param {Object} unregisteredCookies - object containing unregistered cookies
 * @param {Object} logger - a logger instance
 * @param {bool} isPostExpire - boolean indicating if we should log pre/post warning message
 */
export const logUnregisteredCookies = (
  unregisteredCookies,
  logger,
  isPostExpire
) => {
  for (const [cookieName, cookieValue] of Object.entries(unregisteredCookies)) {
    logger.warn({
      name: 'Unregistered Cookie',
      message: `Cookie ${cookieName} was detected in frontend logging ${
        isPostExpire ? 'AFTER expiration' : 'BEFORE expiration'
      }. This cookie does not exist in the list of approved cookies.`,
      data: {
        cookieName,
        cookieValue,
      },
    });
  }
};

/**
 * Log cookies that violate user preferences
 *
 * @param {Object} prohibitedCookies - object containing unregistered cookies
 * @param {Object} logger - a logger instance
 * @param {bool} isPostExpire - boolean indicating if we should log pre/post warning message
 */
export const logProhibitedCookies = (
  prohibitedCookies,
  logger,
  isPostExpire
) => {
  for (const [cookieName, cookieValue] of Object.entries(prohibitedCookies)) {
    logger.warn({
      name: 'Prohibited Cookie',
      message: `Cookie ${cookieName} was detected in frontend logging ${
        isPostExpire ? 'AFTER expiration' : 'BEFORE expiration'
      }. This cookie violates user preferences.`,
      data: {
        cookieName,
        cookieValue,
      },
    });
  }
};

/**
 * getCookieDomain
 *
 * This function returns the cookie domain for a given hostname.
 * This is basically a direct port of the PHP function get_top_domain.
 */
export const getCookieDomain = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const hostname = window.location.hostname;
  const domainPieces = hostname.split('.');

  // DEV specific urls
  if (hostname.includes('csnzoo.com')) {
    if (domainPieces.length >= 3) {
      return `.${domainPieces.slice(-3, 3).join('.')}`;
    }
  }
  // Special treatment for co.uk due to the multiple TLDs
  if (hostname.includes('co.uk')) {
    if (domainPieces.length === 3) {
      return `.${domainPieces[0]}.${domainPieces[1]}.${domainPieces[2]}`;
    }
    if (domainPieces.length === 4) {
      return `.${domainPieces[1]}.${domainPieces[2]}.${domainPieces[3]}`;
    }
    if (domainPieces.length === 5) {
      return `.${domainPieces[2]}.${domainPieces[3]}.${domainPieces[4]}`;
    }
  }
  // All other usecases
  if (domainPieces.length === 3) {
    return `.${domainPieces[1]}.${domainPieces[2]}`;
  }

  if (domainPieces.length === 4) {
    return `.${domainPieces[2]}.${domainPieces[3]}`;
  }

  if (domainPieces.length === 5) {
    return `.${domainPieces[3]}.${domainPieces[4]}`;
  }
};

/**
 *
 * @param {object} userPreferences - keys: preference types, values: booleans
 * @param {object} registry - keys: cookie names, values: strings OR arrays of strings of allowed preference level types
 */

// go through the registry and return the cookie name if it matches user preferences
export const getAllowedCookies = (userPreferences, registry) => {
  const stringPrefs = Object.keys(userPreferences).filter(
    (el) => userPreferences[el]
  );
  return Object.keys(registry).filter((el) => {
    if (Array.isArray(registry[el])) {
      // If the registry value is an array, check if any of the
      // preferences strings are included in the registry array values
      return stringPrefs.some((v) => registry[el].indexOf(v) !== -1);
    }
    return stringPrefs.includes(registry[el]);
  });
};

/**
 * Cookie API Error Messages
 */

export const COOKIE_REGISTRY_ERROR = new Error(
  'CreateCookieApi: The cookieRegistry is either missing or malformed'
);
export const SUB_COOKIE_REGISTRY_ERROR = new Error(
  'CreateCookieApi: The subcookieRegistry is either missing or malformed'
);
export const USER_PREFERENCES_ERROR = new Error(
  'CreateCookieApi: The initialUserPreferences object is either missing or malformed'
);
export const FILTERING_ERROR = new Error(
  'CreateCookieApi: The isCookieFilteringEnabled boolean is either missing or malformed'
);
export const LOGGING_ERROR = new Error(
  'CreateCookieApi: The shouldLogCookieData boolean is either missing or malformed'
);
